import { styled } from '@mui/material';

import { InputSize, type HeightSize } from '~/constants/form';
import { ResponsiveBreakPoints } from '~/constants/responsive';
import { colors, fontFamily, formSizes } from '~/styles/constants';

interface ButtonProps {
  className?: string;
  disabled: boolean;
  hasOptionSelected: boolean;
  height?: HeightSize;
  isMultiSelect?: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  ref?: (node: React.ReactNode) => void;
  responsive?: boolean;
  size?: number;
  tabIndex?: number;
  type: string;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SelectButtonStyled = styled('button')<ButtonProps & any>`
  background: none;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 3px;
  font-family: ${fontFamily};
  line-height: ${(props) => (props.height ? `${props.height}px` : formSizes.inputHeight)};
  ${(props) =>
    props.isMultiSelect && props.hasOptionSelected
      ? 'height: unset;'
      : `height: ${props.height ? `${props.height}px` : formSizes.inputHeight}`};

  width: 100%;

  /* Tiny screens (iPhone 5) */
  @media screen and (min-width: ${ResponsiveBreakPoints.ExtraSmallMobile}px) {
    min-width: 66px;
  }

  /* Tablet and above */
  @media screen and (min-width: ${ResponsiveBreakPoints.Tablet}px) {
    ${(props) => (props.responsive ? 'width: 100%;' : `width: ${props.size}px;`)};
  }
  text-align: left;
  padding: ${(props) => (props.isMultiSelect ? '3px 3px 0 3px;' : '0 18px 0 6px;')};
  font-size: 14px;
  color: ${(props) => (props.hasOptionSelected ? colors.form.fontColor : colors.placeholderColor)};
  position: relative;
  background-color: white;
  outline: none;

  :focus,
  &:active {
    border-color: ${colors.form.activeBorderColor};
  }

  :disabled {
    background-color: ${colors.form.greyDisabled};
    color: ${colors.form.disabledLabelFontColor};
    border-color: ${colors.form.greyDisabledBorder};
  }
`;

const ButtonIconContainer = styled('span')<{ disabled?: boolean }>`
  position: absolute;
  right: 12px;
  font-size: 12px;
  color: ${(props) => (props.disabled ? colors.form.greyDisabledButton : colors.placeholderColor)};
  top: 50%;
  transform: translateY(-50%);
`;

const TextContainer = styled('div')<{ isMultiSelect?: boolean }>`
  white-space: ${(props) => (props.isMultiSelect ? 'initial' : 'nowrap')};
  line-height: initial;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 30px);
  min-width: 30px;
`;

interface SelectButtonProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buttonRef?: (node: any) => void;
  children?: React.ReactNode;
  className?: string;
  dataAnalytics?: string;
  dataE2E?: string;
  dataPendo?: string;
  dataTestid?: string;
  disabled?: boolean;
  hasOptionSelected?: boolean;
  height?: HeightSize;
  invalid?: boolean;
  isMultiSelect?: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  responsive?: boolean;
  size?: InputSize;
  tabIndex?: number;
  text?: string;
}

const SelectButton = (props: SelectButtonProps) => {
  const {
    buttonRef,
    children,
    className,
    dataAnalytics,
    dataE2E,
    dataPendo,
    dataTestid,
    disabled,
    hasOptionSelected,
    height,
    invalid,
    isMultiSelect,
    onClick,
    responsive,
    size,
    tabIndex,
  } = props;
  const buttonSize = size ? size : InputSize.medium;

  return (
    <SelectButtonStyled
      className={className}
      data-analytics={dataAnalytics}
      data-e2e={dataE2E}
      data-pendo={dataPendo}
      data-testid={dataTestid}
      disabled={!!disabled}
      hasOptionSelected={!!hasOptionSelected}
      height={height}
      isMultiSelect={isMultiSelect}
      onClick={onClick}
      ref={buttonRef}
      responsive={responsive}
      size={buttonSize}
      style={{ borderColor: invalid ? colors.dangerRed : colors.form.greyBorder }}
      tabIndex={tabIndex}
      type="button"
    >
      <TextContainer isMultiSelect={isMultiSelect}>{children}</TextContainer>
      <ButtonIconContainer disabled={disabled}>
        <i className="fa fa-chevron-down" />
      </ButtonIconContainer>
    </SelectButtonStyled>
  );
};

export default SelectButton;
